import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import cn from "@/v2-ui/utils/utils.cn"
import Button from "@/v2-ui/button"
import Input, { type InputProps } from "@/v2-ui/input"
import Textarea from "@/v2-ui/textarea"

type InputGroupSize = "tiny" | "xs" | "sm" | "md" | "lg"
type InputGroupVariant = "outline" | "secondary" | "ghost"

const inputGroupVariants = cva(
  "has-[[data-slot=input-group-control]:focus-visible]:border-ring has-[[data-slot=input-group-control]:focus-visible]:ring-ring/50 has-[[data-slot][aria-invalid=true]]:ring-destructive/20 has-[[data-slot][aria-invalid=true]]:border-destructive dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40 has-disabled:opacity-50 border transition-colors has-[[data-slot=input-group-control]:focus-visible]:ring-[3px] has-[[data-slot][aria-invalid=true]]:ring-[3px] has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3 has-[>[data-align=block-start]]:[&>input]:pb-3 has-[>[data-align=inline-end]]:[&>input]:pr-1.5 has-[>[data-align=inline-start]]:[&>input]:pl-1.5 [[data-slot=combobox-content]_&]:focus-within:border-inherit [[data-slot=combobox-content]_&]:focus-within:ring-0 has-[[data-slot=input-group-addon][data-flush=true]]:overflow-hidden group/input-group relative flex w-full min-w-0 items-center outline-none",
  {
    variants: {
      size: {
        tiny: "h-6 rounded-sm", // 24px
        xs: "h-7 rounded-sm", // 28px
        sm: "h-8 rounded-sm", // 32px
        md: "h-9 rounded-md", // 36px
        lg: "h-10 rounded-md", // 40px
      },
      variant: {
        outline:
          "border-input bg-transparent shadow-sm has-disabled:bg-input/50 dark:has-disabled:bg-input/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground shadow-sm",
        ghost:
          "border-transparent bg-transparent has-[[data-slot=input-group-control]:focus-visible]:border-input has-[[data-slot=input-group-control]:focus-visible]:shadow-sm",
      },
    },
    defaultVariants: {
      size: "sm",
      variant: "outline",
    },
  },
)

type InputGroupProps = React.ComponentProps<"div"> &
  VariantProps<typeof inputGroupVariants>

function InputGroup({
  className,
  size = "sm",
  variant = "outline",
  ...props
}: InputGroupProps) {
  return (
    <div
      data-slot="input-group"
      data-size={size}
      data-variant={variant}
      role="group"
      className={cn(inputGroupVariants({ size, variant }), className)}
      {...props}
    />
  )
}

const inputGroupAddonVariants = cva(
  "text-muted-foreground h-full gap-2 text-sm font-medium group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:rounded-[calc(var(--radius)*0.375)] [&>svg:not([class*='size-'])]:size-4 flex cursor-text items-center justify-center select-none",
  {
    variants: {
      align: {
        "inline-start":
          "pl-2 has-[>button]:ml-[-0.3rem] has-[>kbd]:ml-[-0.15rem] order-first",
        "inline-end":
          "pr-2 has-[>button]:mr-[-0.3rem] has-[>kbd]:mr-[-0.15rem] order-last",
        "block-start":
          "h-auto px-2.5 py-1.5 pt-2 group-has-[>input]/input-group:pt-2 [.border-b]:pb-2 order-first w-full justify-start",
        "block-end":
          "h-auto px-2.5 py-1.5 pb-2 group-has-[>input]/input-group:pb-2 [.border-t]:pt-2 order-last w-full justify-start",
      },
      /**
       * Drops the inset so the addon's content runs edge to edge — a unit
       * picker or segmented control that should read as part of the field
       * rather than a chip floating inside it. Pair with `isFlush` on
       * InputGroupButton; the group clips the corners to its own radius.
       */
      isFlush: {
        true: "p-0 m-0 has-[>button]:m-0 has-[>kbd]:m-0 self-stretch",
        false: "",
      },
    },
    defaultVariants: {
      align: "inline-start",
      isFlush: false,
    },
  },
)

function InputGroupAddon({
  className,
  align = "inline-start",
  isFlush = false,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      data-flush={isFlush}
      className={cn(inputGroupAddonVariants({ align, isFlush }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("button")) {
          return
        }
        e.currentTarget.parentElement?.querySelector("input")?.focus()
      }}
      {...props}
    />
  )
}

/**
 * Button sizes for InputGroupButton, designed to be one step smaller than the input.
 * isSquare forces the button to be square by setting width equal to height.
 */
const inputGroupButtonVariants = cva(
  "gap-2 text-sm shadow-none flex items-center",
  {
    variants: {
      size: {
        tiny: "!h-4 gap-0.5 rounded-[calc(var(--radius)*0.625)] px-1 text-xs [&>svg:not([class*='size-'])]:size-3",
        xs: "!h-5 gap-0.5 rounded-[calc(var(--radius)*0.625)] px-1 text-xs [&>svg:not([class*='size-'])]:size-3",
        sm: "!h-6 gap-1 rounded-[calc(var(--radius)*0.625)] px-1.5 [&>svg:not([class*='size-'])]:size-3.5",
        md: "!h-7 gap-1 rounded-[calc(var(--radius)*0.625)] px-2 [&>svg:not([class*='size-'])]:size-3.5",
        lg: "!h-8 gap-1.5 rounded-[calc(var(--radius)*0.625)] px-2.5 [&>svg:not([class*='size-'])]:size-4",
      },
      isSquare: {
        true: "overflow-hidden !px-0",
        false: "",
      },
      /**
       * Fills the field instead of sitting one step smaller inside it. Square
       * corners: the group clips them to its own radius, so the button keeps
       * the field's shape at every size. Pair with `isFlush` on
       * InputGroupAddon, which drops the surrounding inset.
       */
      isFlush: {
        true: "!h-full self-stretch rounded-none",
        false: "",
      },
    },
    compoundVariants: [
      { size: "tiny", isSquare: true, class: "!w-4 !min-w-4" },
      { size: "xs", isSquare: true, class: "!w-5 !min-w-5" },
      { size: "sm", isSquare: true, class: "!w-6 !min-w-6" },
      { size: "md", isSquare: true, class: "!w-7 !min-w-7" },
      { size: "lg", isSquare: true, class: "!w-8 !min-w-8" },
    ],
    defaultVariants: {
      size: "sm",
      isSquare: false,
      isFlush: false,
    },
  },
)

type InputGroupButtonProps = Omit<
  React.ComponentProps<typeof Button>,
  "size" | "isSquare"
> &
  VariantProps<typeof inputGroupButtonVariants>

function InputGroupButton({
  className,
  type = "button",
  variant = "ghost",
  size = "sm",
  isSquare = false,
  isFlush = false,
  ...props
}: InputGroupButtonProps) {
  return (
    <Button
      type={type}
      data-size={size}
      size={size}
      variant={variant}
      className={cn(
        inputGroupButtonVariants({ size, isSquare, isFlush }),
        className,
      )}
      {...props}
    />
  )
}

function InputGroupText({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      className={cn(
        "text-muted-foreground gap-2 text-sm [&_svg:not([class*='size-'])]:size-4 flex items-center [&_svg]:pointer-events-none",
        className,
      )}
      {...props}
    />
  )
}

/**
 * Input styles based on parent InputGroup size.
 * Uses h-full to fill the container height.
 * Text size and padding vary based on the group size via CSS.
 */
function InputGroupInput({ className, ...props }: Omit<InputProps, "size">) {
  return (
    <Input
      data-slot="input-group-control"
      className={cn(
        // Base styles - fill height, no border
        "!h-full rounded-none border-0 bg-transparent shadow-none ring-0 focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0 dark:bg-transparent dark:disabled:bg-transparent flex-1",
        // Size-specific styles via parent's data-size
        "group-data-[size=tiny]/input-group:px-2 group-data-[size=tiny]/input-group:text-xs",
        "group-data-[size=xs]/input-group:px-2 group-data-[size=xs]/input-group:text-sm",
        "group-data-[size=sm]/input-group:px-3 group-data-[size=sm]/input-group:text-sm",
        "group-data-[size=md]/input-group:px-3 group-data-[size=md]/input-group:text-sm",
        "group-data-[size=lg]/input-group:px-4 group-data-[size=lg]/input-group:text-base",
        className,
      )}
      {...props}
    />
  )
}

function InputGroupTextarea({
  className,
  ...props
}: React.ComponentProps<"textarea">) {
  return (
    <Textarea
      data-slot="input-group-control"
      className={cn(
        "rounded-none border-0 bg-transparent py-2 shadow-none ring-0 focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0 dark:bg-transparent dark:disabled:bg-transparent flex-1 resize-none",
        className,
      )}
      {...props}
    />
  )
}

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupInput,
  InputGroupTextarea,
}

export type { InputGroupSize, InputGroupVariant }
